import { useState } from 'react';
import { Icon } from '../shared';
import { Input } from '../ui';
import { authService } from '@foodbook/core-logic';

interface LoginFormProps {
  onSwitchToRegister: () => void;
  onSwitchToForgotPassword: () => void;
  onClose: () => void;
  onSuccess: (user: any) => void;
  onError?: (error: string) => void;
}

export const LoginForm = ({
  onSwitchToRegister,
  onSwitchToForgotPassword,
  onClose,
  onSuccess,
  onError,
}: LoginFormProps) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email) {
      onError?.('请输入邮箱地址');
      return;
    }
    if (!formData.password) {
      onError?.('请输入密码');
      return;
    }

    setIsLoading(true);
    try {
      const user = await authService.loginWithPassword(formData.email, formData.password);
      onSuccess(user);
    } catch (err: any) {
      onError?.(err?.message || '登录失败，请检查邮箱和密码');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative">
      <div className="absolute -top-3 -left-3 w-6 h-6 border-l-4 border-t-4 border-wood-dark pointer-events-none" />
      <div className="absolute -top-3 -right-3 w-6 h-6 border-r-4 border-t-4 border-wood-dark pointer-events-none" />
      <div className="absolute -bottom-3 -left-3 w-6 h-6 border-l-4 border-b-4 border-wood-dark pointer-events-none" />
      <div className="absolute -bottom-3 -right-3 w-6 h-6 border-r-4 border-b-4 border-wood-dark pointer-events-none" />

      <button
        type="button"
        onClick={onClose}
        className="absolute -top-2 -right-2 z-20 w-8 h-8 flex items-center justify-center bg-wood-medium border-2 border-wood-darker text-wood-darker hover:bg-wood-light active:translate-y-0.5 transition-all jagged-corners"
        style={{ boxShadow: 'inset -2px -2px 0 0 rgba(0,0,0,0.2), inset 2px 2px 0 0 rgba(255,255,255,0.3), 2px 2px 0 0 rgba(0,0,0,0.3)' }}
      >
        <Icon name="close" className="w-4 h-4" />
      </button>

      <div className="space-y-5 p-1">
        <div className="text-center space-y-3">
          <div
            className="inline-flex items-center justify-center w-16 h-16 bg-wood-medium border-2 border-wood-darker jagged-corners relative"
            style={{ boxShadow: 'inset -3px -3px 0 0 rgba(0,0,0,0.2), inset 3px 3px 0 0 rgba(255,255,255,0.2), 3px 3px 0 0 rgba(0,0,0,0.2)' }}
          >
            <Icon name="restaurant_menu" className="w-8 h-8 text-primary drop-shadow-sm" />
            <div className="absolute -top-1 -right-1 w-2 h-2 bg-ui-highlight animate-pulse" />
          </div>

          <div>
            <h2 className="text-xl font-bold text-wood-darker tracking-wide" style={{ textShadow: '1px 1px 0 rgba(0,0,0,0.1)' }}>
              欢迎回来
            </h2>
            <p className="text-wood-dark mt-1 text-sm">自托管版本当前使用邮箱密码登录</p>
          </div>
        </div>

        <div className="relative bg-wood-light/30 border-2 border-wood-medium p-3 jagged-corners text-center text-sm text-wood-dark">
          验证码登录已在迁移阶段下线，后续可按需要补回。
        </div>

        <form onSubmit={handlePasswordLogin} className="space-y-4">
          <div className="space-y-1.5">
            <label className="flex items-center gap-1.5 text-sm font-bold text-wood-darker">
              <Icon name="email" className="w-4 h-4 text-wood-dark" />
              邮箱地址
            </label>
            <Input
              type="email"
              value={formData.email}
              onChange={handleInputChange('email')}
              placeholder="请输入邮箱地址"
              disabled={isLoading}
            />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-1.5 text-sm font-bold text-wood-darker">
                <Icon name="lock_outline" className="w-4 h-4 text-wood-dark" />
                密码
              </label>
              <button
                type="button"
                onClick={onSwitchToForgotPassword}
                className="text-xs font-bold text-primary hover:text-primary-dark transition-colors underline underline-offset-2"
              >
                忘记密码？
              </button>
            </div>
            <Input
              type="password"
              value={formData.password}
              onChange={handleInputChange('password')}
              placeholder="请输入密码"
              disabled={isLoading}
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 bg-primary text-white font-bold border-2 border-wood-darker jagged-corners hover:brightness-110 disabled:opacity-60"
          >
            {isLoading ? '登录中...' : '登录'}
          </button>
        </form>

        <div className="text-center text-sm text-wood-dark">
          还没有账号？
          <button type="button" onClick={onSwitchToRegister} className="ml-2 font-bold text-primary underline underline-offset-2">
            立即注册
          </button>
        </div>
      </div>
    </div>
  );
};
